import {
  type IActivityOccurrence,
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import styles from "./ActivityItem.module.scss";
import { ActivityTag } from "@/components/ActivityTag/ActivityTag";
import { getPatientNameWithStatus } from "@/api/Patients";
import { Time } from "@/components/Time/Time";
import { isTomorrow } from "date-fns";
import { useLingui } from "@lingui/react/macro";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import ActivityDescriptionIcon from "@medoma/components/icons/ActivityDescriptionIcon";
import CommentIcon from "@medoma/components/icons/CommentIcon";
import { OutlinedButton } from "@medoma/components/Button/Button";
import { finishActivityOccurrence } from "@/api/Activities";
import {
  deducedError,
  displayErrorMessageAlert,
  isErrorWithKnownErrorCode,
  knownErrorCodeSchema,
} from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trans } from "@lingui/react/macro";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { useSelectedDateIsToday } from "@/Utils/useSelectedDate";

export const ActivityItem = ({
  activity,
}: {
  activity: IActivityOccurrence;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const allowFinishActivityOccurrence = useSelectedDateIsToday();

  const {
    mutate: finishActivityOccurrenceMutation,
    isPending: isFinishingActivityOccurrence,
  } = useMutation({
    mutationFn: ({
      activityId,
      occurrenceId,
    }: {
      activityId: string;
      occurrenceId: string;
    }) => finishActivityOccurrence(activityId, occurrenceId),
    // MED-2243 - prefer other ways of resolving errors than displaying alerts
    onError: (error) => {
      if (
        isErrorWithKnownErrorCode(error) &&
        error.data.code === knownErrorCodeSchema.enum.OccurrenceFinished
      ) {
        return queryClient.invalidateQueries();
      } else {
        displayErrorMessageAlert(
          `${t`Det gick inte att markera aktivitetstillfället som klart.`} ${deducedError(
            error,
          )}`,
        );
      }
    },
    onSuccess: () => {
      return queryClient.invalidateQueries();
    },
  });

  const anyTime = t(timeOfDayDictionary.Any.short);
  const tomorrowAnyTime = t`Imorgon ${anyTime}`;
  return (
    <article className={styles.activityItem}>
      {activity.status !== "finished" ? (
        <>
          <div className={styles.headingAndTime}>
            <ActivityTag
              category={activity.category}
              title={
                activity.patient
                  ? getPatientNameWithStatus(activity.patient, "full")
                  : activity.title
              }
              patientId={activity.patient ? activity.patient.id : undefined}
            />
            <Time>
              {isTomorrow(activity.start) &&
              activity.timeOfDay === timeOfDaySchema.enum.Any
                ? tomorrowAnyTime
                : activity.timeOfDay === timeOfDaySchema.enum.Any
                  ? anyTime
                  : formattedTimeSpan(activity.start, activity.end)}
            </Time>
          </div>
          <div className={styles.titleAndDescription}>
            <div className={styles.title}>
              {activity.patient ? (
                <Text element="div" weight="medium">
                  {activity.title}
                </Text>
              ) : null}
            </div>
            {activity.description ? (
              <div className={styles.description}>
                <ActivityDescriptionIcon />
                <TextWithLineBreaks size="small" color="faded">
                  {activity.description}
                </TextWithLineBreaks>
              </div>
            ) : null}
            {activity.note ? (
              <div className={styles.description}>
                <CommentIcon />
                <TextWithLineBreaks size="small" color="faded">
                  {activity.note}
                </TextWithLineBreaks>
              </div>
            ) : null}
          </div>
          {allowFinishActivityOccurrence ? (
            <footer className={styles.footer}>
              <OutlinedButton
                disabled={isFinishingActivityOccurrence}
                onClick={() =>
                  finishActivityOccurrenceMutation({
                    activityId: activity.activityId,
                    occurrenceId: activity.id,
                  })
                }
                width="fill-container"
              >
                <Trans>Markera som klar</Trans>
              </OutlinedButton>
            </footer>
          ) : null}
        </>
      ) : (
        !!activity.finishedAt &&
        (() => {
          return (
            <div className={styles.headingAndTime}>
              <ActivityTag
                category={activity.category}
                title={
                  activity.patient
                    ? getPatientNameWithStatus(activity.patient, "full")
                    : activity.title
                }
                patientId={activity.patient ? activity.patient.id : undefined}
              />
              <StatusTag
                status="finished"
                timestamp={activity.finishedAt}
                variant="icon-and-text-with-timestamp"
              />
            </div>
          );
        })()
      )}
    </article>
  );
};
